/**
 * Admin SPA analytics → YOM v2 (project T-APP, see
 * aapastech-backend/src/lib/analytics/tracking-plan.ts and
 * aapastech-backend/docs/platform-analytics.md).
 *
 * - Browser SDK `@yourownmetric/sdk` >= 0.3 (wire protocol v2, POST /v2/collect).
 * - Off unless `VITE_YOM_APP_PK` (a publishable `yom_pk_` key for T-APP) is set.
 * - page_view on every SPA route change comes from the SDK's pushState hook.
 * - identify() sends the internal user id ONLY — never email, name or role.
 * - One writer per fact: sign_up, login, project_created, plugin_installed,
 *   subscriptions… are written by the backend. This file only sends the
 *   client-only events below. Do not add a client copy of a server event.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { YOM } from '@yourownmetric/sdk';

const YOM_APP_PK: string | undefined = import.meta.env.VITE_YOM_APP_PK;
const YOM_INGEST_URL: string | undefined = import.meta.env.VITE_YOM_INGEST_URL;
const IS_DEV = import.meta.env.DEV;

/** Client events in the tracking plan (side: 'client'), with their props. */
export interface ClientEvents {
  editor_opened: { mode?: 'studio' | 'editor' | 'blog' };
  report_viewed: {
    report: 'analytics' | 'orders' | 'crm' | 'leads' | 'chatbot_quality' | 'errors' | 'seo' | 'aeo';
  };
  upgrade_clicked: { source: string; target_plan?: string };
  plugin_marketplace_viewed: Record<string, never>;
}

let initialized = false;

/** Initialize once at app boot (main.tsx). No-op without a key. */
export function initAnalytics(): void {
  if (initialized) return;
  if (!YOM_APP_PK || !/^yom_pk_/.test(YOM_APP_PK)) {
    if (IS_DEV) console.warn('[Analytics] VITE_YOM_APP_PK not set — analytics disabled');
    return;
  }
  YOM.init({
    apiKey: YOM_APP_PK,
    ...(YOM_INGEST_URL ? { ingestUrl: YOM_INGEST_URL } : {}),
    autoCapture: {
      pageviews: true, // SPA-aware (pushState + popstate)
      engagement: true,
      errors: true,
      performance: true,
      clicks: false,
      behavior: false,
      ecommerce: false,
    },
    // Cookieless (no stored visitor id) until a consent decision exists.
    consent: { default: 'unknown' },
    privacy: { respectDNT: true },
    debug: IS_DEV,
  });
  initialized = true;
}

/** Link the session to the internal user id. Nothing else leaves the browser. */
export function identifyUser(user: { id?: string; _id?: string } | null | undefined): void {
  if (!initialized) return;
  const id = user?.id ?? user?._id;
  if (id) YOM.identify(String(id));
}

/** Clear identity on logout. */
export function resetUser(): void {
  if (!initialized) return;
  YOM.reset();
}

/** Track a tracking-plan client event. Fire-and-forget, batched by the SDK. */
export function trackEvent<K extends keyof ClientEvents>(name: K, props: ClientEvents[K]): void {
  if (!initialized) return;
  YOM.track(name, props as Record<string, unknown>);
}

/** Route → view event. Kept here so pages need no analytics code. */
export function routeEvent(
  pathname: string
): { [K in keyof ClientEvents]: [K, ClientEvents[K]] }[keyof ClientEvents] | null {
  const p = pathname.replace(/\/+$/, '');
  if (/^\/projects\/[^/]+\/editor\//.test(p)) return ['editor_opened', { mode: 'editor' }];
  if (/^\/projects\/[^/]+\/blog\/(new|[^/]+\/edit)$/.test(p)) {
    return ['editor_opened', { mode: 'blog' }];
  }
  const reports: Array<[RegExp, ClientEvents['report_viewed']['report']]> = [
    [/^\/projects\/[^/]+\/analytics$/, 'analytics'],
    [/^\/projects\/[^/]+\/ecommerce\/orders$/, 'orders'],
    [/^\/projects\/[^/]+\/crm$/, 'crm'],
    [/^\/projects\/[^/]+\/contacts$/, 'leads'],
    [/^\/projects\/[^/]+\/chatbot$/, 'chatbot_quality'],
    [/^\/projects\/[^/]+\/errors$/, 'errors'],
    [/^\/projects\/[^/]+\/plugins\/seo-copilot\/dashboard$/, 'seo'],
    [/^\/projects\/[^/]+\/plugins\/aeo\/dashboard$/, 'aeo'],
  ];
  for (const [re, report] of reports) if (re.test(p)) return ['report_viewed', { report }];
  if (/^(\/projects\/[^/]+)?\/plugins$/.test(p)) return ['plugin_marketplace_viewed', {}];
  return null;
}

/** Mount once inside the router (App.tsx). Emits view events on route change. */
export function useRouteAnalytics(): void {
  const { pathname } = useLocation();
  useEffect(() => {
    const ev = routeEvent(pathname);
    if (ev) trackEvent(ev[0], ev[1] as never);
  }, [pathname]);
}
